import React, { useTransition } from 'react';
import { useSelector } from 'react-redux';

import { ApolloClient } from '@apollo/client';

import classNames from 'classnames';

import AppLink from '@/components/AppLink/AppLink';
import Card from '@/components/Card';
import { ReactivationFlow } from '@/components/ReactivationFlow/ReactivationFlow';
import { SignInForm } from '@/components/SignIn';
import { SocialSignupFlow } from '@/components/SocialSignupFlow/SocialSignupFlow';
import Spinner from '@/components/Spinner';
import { LOGIN_WITH_RESERVATION_PAGE, RESET_PASSWORD_PAGE, SIGNUP_PAGE } from '@/constants';
import { useSignInContext } from '@/contexts/SignInContext';
import { SocialMediaType } from '@/helpers/enumsTypes';
import { useEnvContext } from '@/helpers/envUtils';
import { useAppRouter } from '@/hooks/useAppRouter';
import useAppUiData from '@/hooks/useAppUiData';
import { useRedirectUrl } from '@/hooks/useRedirectUrl';
import { selectFeatureFlags, selectSettings } from '@/reducers/common/selectors';
import { SignInFormData } from '@/schemas/signInSchema';

import styles from './page.module.scss';

import useResourcesData from '../../iframe/(tabs)/signin/useResourcesData';

type SocialFormData = {
  firstname: string;
  lastname: string;
  email: string;
  socialMediaId: string;
  dob: string;
  type: SocialMediaType;
  picture: string;
  password: string;
  keepLoggedIn: boolean;
};

interface SignInPageContentProps {
  isShowReactivationFlow: boolean;
  reactivationClient: ApolloClient<any> | null;
  exitReactivationFlow: () => void;
  isRedirectedFromSocialLogin: boolean;
  onSignInSubmit: (_data: SignInFormData) => Promise<void>;
  onSocialLogin: (_formFieldValues: any) => Promise<void>;
  onSocialSignupConfirm: (_formData: any) => Promise<void>;
  socialFormState: SocialFormData;
  customFooter?: React.ReactNode;
  customRedirectURL?: string;
  claimBookingCredentials?: SignInFormData;
}

export const SignInPageContent: React.FC<SignInPageContentProps> = ({
  isShowReactivationFlow,
  reactivationClient,
  exitReactivationFlow,
  isRedirectedFromSocialLogin,
  onSignInSubmit,
  onSocialLogin,
  onSocialSignupConfirm,
  socialFormState,
  customFooter,
  customRedirectURL,
  claimBookingCredentials,
}) => {
  const { BASE_URL } = useEnvContext();
  const { buildFullURL, pushWithRedirect, redirectUrl } = useRedirectUrl();
  const router = useAppRouter();
  const [isPending, startTransition] = useTransition();
  const { showErrorBox, errorMsg, setServerErrorMsg, isFromIframe } = useSignInContext();
  const appUiData = useAppUiData();
  const { appleClientId, googleAppId, facebookId } = useSelector(selectSettings);
  const { isLoginByResIdEnabled } = useSelector(selectFeatureFlags);
  const resourcesData = useResourcesData();

  const handleLoginWithRes = () => {
    startTransition(() => {
      if (redirectUrl) {
        pushWithRedirect(LOGIN_WITH_RESERVATION_PAGE);
      } else {
        router.push(LOGIN_WITH_RESERVATION_PAGE);
      }
    });
  };

  const resetRedirect = () => buildFullURL(RESET_PASSWORD_PAGE);
  const signupRedirect = () => buildFullURL(SIGNUP_PAGE);

  const Wrapper = isFromIframe ? 'div' : Card;

  if (isPending) {
    return <Spinner />;
  }

  return (
    <div className={isFromIframe ? styles.iframePageContainer : styles.container}>
      {isShowReactivationFlow ? (
        <ReactivationFlow
          client={reactivationClient}
          onBackToLogin={exitReactivationFlow}
          className={styles.reactivationCard}
        />
      ) : (
        <>
          <Wrapper className={isFromIframe ? styles.iframeWrapper : styles.card}>
            {!isRedirectedFromSocialLogin ? (
              <SignInForm
                onSignInSubmit={onSignInSubmit}
                showErrorBox={showErrorBox}
                errorMsg={errorMsg}
                settingData={{ appleClientId, googleAppId, facebookId }}
                resourcesData={resourcesData}
                onSocialLogin={onSocialLogin}
                redirectURL={customRedirectURL || `${BASE_URL}/auth/v1/signin`}
                onLoginByReservationId={handleLoginWithRes}
                isLoginByResIdEnabled={isLoginByResIdEnabled}
                appUiData={appUiData}
                setServerErrorMsg={setServerErrorMsg}
                claimBookingCredentials={claimBookingCredentials}
              />
            ) : (
              <SocialSignupFlow
                resourcesData={resourcesData}
                onSubmit={onSocialSignupConfirm}
                initialFormState={socialFormState}
              />
            )}
          </Wrapper>

          {customFooter || (
            <div className={styles.footer}>
              <AppLink href={resetRedirect()} className={classNames(styles.footerLink, styles.resetPasswordLink)}>
                Forgot your password?
              </AppLink>
              <div className={styles.signUpLinkWrapper}>
                Don&apos;t have an account?
                <br />
                <AppLink href={signupRedirect()} className={classNames(styles.footerLink, styles.signUpLink)}>
                  Sign Up
                </AppLink>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
